import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

/**
 * AccountingSettingsModal
 *
 * Superadmin-only modal accessible from the AdminPanel Settings tab.
 * Currently manages the CAD ↔ USD exchange rate used in Maestro 9091 reports.
 * Additional accounting configuration items can be added here over time.
 *
 * Access: Superadmin only (frontend gate + API route enforcement).
 */
const AccountingSettingsModal = ({ isOpen, onClose }) => {
  const { instance, accounts } = useMsal();

  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedApiCall(instance, accounts, 'get', '/maestro-reports/exchange-rate');
      setRates(response.data.data || []);
    } catch (err) {
      setError('Failed to load exchange rates.');
      console.error('Error fetching exchange rates:', err);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  useEffect(() => {
    if (isOpen) fetchRates();
  }, [isOpen, fetchRates]);

  if (!isOpen) return null;

  const cadUsd = rates.find(r => r.from_currency === 'CAD' && r.to_currency === 'USD');
  const usdCad = rates.find(r => r.from_currency === 'USD' && r.to_currency === 'CAD');

  const formatDate = (ts) =>
    ts
      ? new Date(ts).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      : '—';

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        width: '560px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e9ecef',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f8f9fa',
          borderRadius: '12px 12px 0 0',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1a202c' }}>
              💰 Accounting Settings
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#6b7280' }}>
              Superadmin — Manage accounting-related configuration
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '22px',
              cursor: 'pointer',
              color: '#6b7280',
              lineHeight: 1,
              padding: '4px 8px',
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
              Loading…
            </div>
          )}

          {error && !loading && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fca5a5',
              borderRadius: '6px',
              color: '#dc2626',
              marginBottom: '16px',
              fontSize: '14px',
            }}>
              {error}
            </div>
          )}

          {!loading && (
            <>
              {/* Section: Currency Exchange Rates */}
              <div style={{ marginBottom: '8px' }}>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: '600', color: '#374151' }}>
                  Currency Exchange Rates
                </h3>
                <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#6b7280' }}>
                  Set the CAD ↔ USD rate used when generating 9091 reports for multi-currency projects.
                  Updating CAD → USD automatically updates the inverse (USD → CAD) rate.
                </p>

                {/* Rate row */}
                <ExchangeRateRow
                  label="CAD → USD"
                  rowData={cadUsd}
                  formatDate={formatDate}
                  instance={instance}
                  accounts={accounts}
                  fromCurrency="CAD"
                  toCurrency="USD"
                  onSaved={fetchRates}
                />

                {/* Inverse — read-only display */}
                {usdCad && (
                  <div style={{
                    marginTop: '10px',
                    padding: '10px 14px',
                    backgroundColor: '#f1f5f9',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap',
                    fontSize: '13px',
                  }}>
                    <span style={{ fontWeight: '500', color: '#374151' }}>USD → CAD (auto-calculated):</span>
                    <span style={{ fontFamily: 'monospace', color: '#1d4ed8', fontWeight: '600' }}>
                      {parseFloat(usdCad.rate).toFixed(6)}
                    </span>
                    <span style={{ color: '#9ca3af', marginLeft: '4px' }}>
                      Last updated: {formatDate(usdCad.updated_at)}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e9ecef',
          display: 'flex',
          justifyContent: 'flex-end',
          backgroundColor: '#f8f9fa',
          borderRadius: '0 0 12px 12px',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 20px',
              backgroundColor: '#6b7280',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Isolated rate editor row — manages its own input state so keystrokes
 * don't trigger parent re-renders.
 */
const ExchangeRateRow = React.memo(({ label, rowData, formatDate, instance, accounts, fromCurrency, toCurrency, onSaved }) => {
  const initialRate = rowData ? String(parseFloat(rowData.rate).toFixed(6)) : '';
  const [input, setInput] = useState(initialRate);
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (rowData) setInput(String(parseFloat(rowData.rate).toFixed(6)));
  }, [rowData]);

  const handleSave = async () => {
    setFieldError(null);
    setSuccess(false);
    const val = parseFloat(input);
    if (isNaN(val) || val <= 0) {
      setFieldError('Rate must be a positive number (e.g. 0.720000).');
      return;
    }
    if (val >= 100) {
      setFieldError('Rate must be less than 100.');
      return;
    }
    if (val < 0.4 || val > 2.0) {
      setFieldError('That rate looks unusual — CAD/USD is typically between 0.40 and 2.00. Double-check before saving.');
      return;
    }
    setSaving(true);
    try {
      await authenticatedApiCall(instance, accounts, 'put', '/maestro-reports/exchange-rate', {
        from_currency: fromCurrency,
        to_currency: toCurrency,
        rate: val,
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 4000);
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Failed to save rate';
      setFieldError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      padding: '14px 16px',
      backgroundColor: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151', minWidth: '90px' }}>
          {label}
        </span>
        <input
          type="number"
          step="0.000001"
          min="0.001"
          max="99.999"
          value={input}
          onChange={(e) => { setInput(e.target.value); setFieldError(null); setSuccess(false); }}
          onKeyDown={(e) => e.key === 'Enter' && handleSave()}
          style={{
            width: '130px',
            padding: '6px 10px',
            border: fieldError ? '1px solid #ef4444' : '1px solid #d1d5db',
            borderRadius: '5px',
            fontSize: '13px',
            fontFamily: 'monospace',
          }}
          placeholder="e.g. 0.720000"
        />
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '6px 16px',
            backgroundColor: saving ? '#9ca3af' : '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            fontSize: '13px',
            fontWeight: '500',
            cursor: saving ? 'not-allowed' : 'pointer',
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {rowData && (
          <span style={{ fontSize: '12px', color: '#9ca3af' }}>
            Last updated: {formatDate(rowData.updated_at)}
            {rowData.updated_by_name ? ` by ${rowData.updated_by_name}` : ''}
          </span>
        )}
      </div>
      {fieldError && (
        <div style={{ marginTop: '6px', fontSize: '12px', color: '#ef4444' }}>{fieldError}</div>
      )}
      {success && (
        <div style={{ marginTop: '6px', fontSize: '12px', color: '#059669', fontWeight: '500' }}>
          ✓ Rate saved — USD → CAD inverse updated automatically
        </div>
      )}
    </div>
  );
});

export default AccountingSettingsModal;
